import React, { useState } from 'react';

import { Box } from '@rover/kibble/core';
import { HybridOnPressHandler } from '@rover/kibble/core/utils';
import { PriceTransparencyLevels } from '@rover/types/src/PriceTransparencyLevel';

import { FeeExplanationModal } from '../../../../search/SearchPage/components/Modals/FeeExplanationModal';
import { PtCondensedBanner } from '../../../../search/SearchPage/components/PtCondensedBanner';
import { useRevampBrightHorizonsBanner } from '../../hooks/useRevampBrightHorizonsBanner';
import { useRevampCta } from '../../hooks/useRevampCta';
import { useRevampCtaCommon } from '../../hooks/useRevampCtaCommon';
import { SitterPriceBanner } from '../SitterPriceBanner';

import { CTAPropsCommon } from './RevampServicesCta';

type Props = Omit<CTAPropsCommon, 'locale'> & {
  variant?: 'card' | 'sheet' | 'bar';
  onPress?: () => void;
};

const VERTICAL_SPACING = {
  card: { mt: '6x', mb: '-8x' },
  sheet: { mt: '6x', mb: '-6x' },
  bar: { mt: '-4x', mb: '4x' },
} as const;

const CLIPPED_CORNERS = {
  card: {
    overflow: 'hidden',
    borderBottomLeftRadius: '16px',
    borderBottomRightRadius: '16px',
  },
  bar: {
    overflow: 'hidden',
    borderTopLeftRadius: 'inherit',
    borderTopRightRadius: 'inherit',
  },
  sheet: undefined,
} as const;

const RevampCtaPtBanner = ({
  priceTransparencyLevel,
  petType,
  petCount,
  bookingFeePercentage,
  variant = 'card',
  onPress,
}: Props): JSX.Element | null => {
  const { serviceType, selectedService } = useRevampCta();
  const { isPtFull, isPtCondensed, countryConfigUrls, petTypeServices } = useRevampCtaCommon(
    priceTransparencyLevel,
    petType,
    petCount,
    selectedService
  );
  const { isShowingBhBanner } = useRevampBrightHorizonsBanner(petType, petCount);
  const [isFeeExplanationModalOpen, setIsFeeExplanationModalOpen] = useState(false);

  const handlePress: HybridOnPressHandler | undefined = onPress
    ? ({ webEvent }) => {
        if ((webEvent?.target as HTMLElement | undefined)?.closest('a')) return;
        onPress();
      }
    : undefined;

  if (!isPtCondensed && !isPtFull) return null;
  // A Bright Horizons user reads their eligibility banner instead of the full
  // price breakdown, the way the other CTA layouts hand it over.
  if (isPtFull && !isPtCondensed && isShowingBhBanner) return null;

  return (
    <>
      <Box
        data-testid="revamp-cta-pt-banner"
        mx="-4x"
        {...VERTICAL_SPACING[variant]}
        onPress={handlePress}
        sx={{ ...CLIPPED_CORNERS[variant], ...(handlePress ? { cursor: 'pointer' } : {}) }}
      >
        {isPtCondensed ? (
          <PtCondensedBanner
            priceTransparencyLevel={priceTransparencyLevel}
            bookingFeePercentage={bookingFeePercentage}
            onClick={() => setIsFeeExplanationModalOpen(true)}
            isContainerPressable={!handlePress}
          />
        ) : (
          <SitterPriceBanner
            petCounts={petCount}
            petType={petType}
            serviceType={serviceType}
            countryConfigUrls={countryConfigUrls}
            bookingFeePercentage={bookingFeePercentage}
            services={petTypeServices}
            version="revamp"
            isFlush={variant === 'bar'}
          />
        )}
      </Box>
      <FeeExplanationModal
        isOpen={isFeeExplanationModalOpen}
        onClose={() => setIsFeeExplanationModalOpen(false)}
        priceTransparencyLevel={priceTransparencyLevel || PriceTransparencyLevels.lite_condensed}
        bookingPercentageFee={bookingFeePercentage}
      />
    </>
  );
};

export default RevampCtaPtBanner;
